import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Lock, Calculator, Cpu, GraduationCap, Factory, UtensilsCrossed, PartyPopper, Scissors, HeartPulse, Dumbbell, ShoppingBag, Megaphone, Briefcase, Users, Scale, Home, Car, Truck, Plane, Newspaper, Rocket, Store, Brush, Baby, Tractor, Leaf, HandHeart, Landmark, Shield, PawPrint, Palette, Target, Laptop } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo, breadcrumbSchema } from "../lib/useSeo";
import { CATEGORY_META, HELP_GROUPS } from "../lib/categoryMeta";
import ShowcaseGallery from "../components/ShowcaseGallery";

const ICONS = { Calculator, Cpu, GraduationCap, Factory, UtensilsCrossed, PartyPopper, Scissors, HeartPulse, Dumbbell, ShoppingBag, Megaphone, Briefcase, Users, Scale, Home, Car, Truck, Plane, Newspaper, Rocket, Store, Brush, Baby, Tractor, Leaf, HandHeart, Landmark, Shield, PawPrint, Palette, Target, Laptop };

export default function CategoriesPage() {
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [helpGroup, setHelpGroup] = useState("");
  const [q, setQ] = useState("");
  const [cmdResults, setCmdResults] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Search by Category — AI Prompt Shortcuts for Work, Business, Money & Family",
    description: "What do you need help with today? Browse 38 prompt categories and 1,000+ AI short-commands — from business, finance and construction to parenting, events and community life.",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Search by Category", path: "/categories" }]),
  });

  useEffect(() => {
    let alive = true;
    const load = (path, set, attempt = 0) => {
      api.get(path).then((r) => { if (alive) set(r.data); }).catch(() => {
        if (alive && attempt < 5) setTimeout(() => load(path, set, attempt + 1), 2000 * (attempt + 1));
      });
    };
    load("/industries", setIndustries);
    load("/categories", setCategories);
    return () => { alive = false; };
  }, []);

  const isMember = user && (user.membership !== "free" || user.role === "admin");

  useEffect(() => {
    const ql = q.trim();
    if (ql.length < 2) { setCmdResults([]); return; }
    const t = setTimeout(() => {
      const ep = isMember ? `/commands?q=${encodeURIComponent(ql)}` : `/demo-search?q=${encodeURIComponent(ql)}`;
      api.get(ep).then((r) => setCmdResults((r.data || []).slice(0, 8))).catch(() => setCmdResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, isMember]);

  const goCommand = (name) => {
    if (isMember) navigate(`/app?q=${encodeURIComponent(name)}`);
    else if (user && !user.trial_used) navigate("/start-trial");
    else navigate("/pricing");
  };
  const goCategory = (name) => {
    if (isTrial && !trialCats.includes(name)) {
      navigate(`/app?category=${encodeURIComponent(name)}`);
      return;
    }
    if (isMember) navigate(`/app?category=${encodeURIComponent(name)}`);
    else if (user && !user.trial_used) navigate("/start-trial");
    else navigate("/pricing");
  };
  const isTrial = user && user.membership === "trial";
  const trialCats = (user && user.trial_categories) || [];
  const isCategoryLocked = (name) => isTrial && !trialCats.includes(name);
  const shownCategories = helpGroup ? categories.filter((c) => (HELP_GROUPS[helpGroup] || []).includes(c.name)) : categories;

  const results = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return null;
    const tokens = ql.split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
    const textMatch = (text) => {
      const tl = text.toLowerCase();
      if (tl.includes(ql)) return true;
      const words = tl.split(/[^a-z0-9]+/).filter((w) => w.length >= 4);
      return tokens.some((t) => words.some((w) => w.startsWith(t) || t.startsWith(w)));
    };
    const out = [];
    for (const ind of industries) {
      const nameHit = textMatch(ind.name);
      const subHits = ind.subcategories.filter((s) => textMatch(s.name));
      if (nameHit || subHits.length) out.push({ ind, sub: !nameHit && subHits.length ? subHits[0] : null, subHits });
    }
    return out;
  }, [q, industries]);

  const go = (ind, sub) => {
    const sel = { industry: ind.slug, industryName: ind.name, sub: sub?.slug || "", subName: sub?.name || "" };
    localStorage.setItem("aish_pending_industry", JSON.stringify(sel));
    const isMember = user && (user.membership !== "free" || user.role === "admin");
    if (isMember) navigate(`/app?industry=${sel.industry}${sel.sub ? `&sub=${sel.sub}` : ""}`);
    else navigate(`/industry/${ind.slug}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <section className="bg-[#0B0F0D] text-white py-14 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-display text-[#D4AF37] font-bold text-xs sm:text-sm tracking-[0.16em] uppercase">Universal Short-Command Prompt Dictionary™</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 leading-tight">What do you need help with today?</h1>
          <div className="flex flex-wrap justify-center gap-2 mt-6" data-testid="help-group-pills">
            {Object.keys(HELP_GROUPS).map((g) => (
              <button key={g} data-testid={`help-pill-${g.toLowerCase()}`}
                onClick={() => {
                  setHelpGroup(helpGroup === g ? "" : g);
                  setTimeout(() => document.querySelector('[data-testid="prompt-category-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                }}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${helpGroup === g ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow" : "bg-white/10 text-white border border-white/15 hover:border-[#D4AF37]/60"}`}>
                {g}
              </button>
            ))}
          </div>
          <p className="text-gray-300 mt-6 text-sm sm:text-base max-w-xl mx-auto">Pick an area above, browse every prompt category below, or search by your industry.</p>
          <form className="mt-8 max-w-xl mx-auto flex gap-2"
            onSubmit={(e) => { e.preventDefault(); document.querySelector('[data-testid="industry-search-results"], [data-testid="industry-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input data-testid="industry-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search commands, industries and categories…"
                className="w-full bg-white border-2 border-transparent focus:border-[#D4AF37] rounded-2xl pl-12 pr-4 py-4 text-base text-slate-900 shadow-xl focus:outline-none transition-colors" />
            </div>
            <button type="submit" data-testid="industry-search-btn"
              className="shrink-0 bg-gradient-to-r from-amber-500 to-yellow-500 hover:brightness-105 active:scale-[0.98] text-slate-950 font-bold px-5 sm:px-7 rounded-2xl text-sm sm:text-base shadow-xl transition-all">
              Search
            </button>
          </form>
          <p className="text-xs text-gray-500 mt-3">Try: Hair Extensions · Fintech · Manufacturing · Restaurants · Property · SaaS · Accounting</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {results && (
          <div className="max-w-xl mx-auto mb-12" data-testid="industry-search-results">
            {cmdResults.length > 0 && (
              <div className="mb-6" data-testid="command-search-results">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Matching shortcut commands</p>
                <div className="space-y-2">
                  {cmdResults.map((c) => (
                    <button key={c.slug || c.name} data-testid={`command-result-${(c.slug || c.name).replace(/[^a-z0-9-]/gi, "")}`}
                      onClick={() => goCommand(c.name)}
                      className="w-full bg-white border border-slate-200 hover:border-emerald-500/50 rounded-xl px-4 py-3 flex items-center gap-3 text-left transition-all rise">
                      <span className="font-mono font-bold text-sm text-emerald-700 shrink-0">{c.name}</span>
                      <span className="text-xs text-slate-400 truncate flex-1">{c.category}</span>
                      <ArrowRight size={14} className="text-slate-300 shrink-0" />
                    </button>
                  ))}
                </div>
                {isMember ? (
                  <button onClick={() => navigate(`/app?q=${encodeURIComponent(q.trim())}`)} data-testid="search-full-dictionary-btn"
                    className="mt-3 w-full text-center text-sm font-bold text-emerald-700 hover:text-emerald-800 py-2">
                    Search the full dictionary for “{q.trim()}” →
                  </button>
                ) : (
                  <button onClick={() => navigate(user && !user.trial_used ? "/start-trial" : "/pricing")} data-testid="search-unlock-btn"
                    className="mt-3 w-full text-center text-sm font-bold text-emerald-700 hover:text-emerald-800 py-2">
                    {user && !user.trial_used ? "Start free 24-hour pass →" : "Unlock all 1,000+ shortcut commands →"}
                  </button>
                )}
              </div>
            )}
            {results.length === 0 && cmdResults.length === 0 && (
              <p className="text-center text-slate-400 text-sm">No matching commands or industries. Try a broader term like "video", "beauty" or "finance".</p>
            )}
            {results.length > 0 && cmdResults.length > 0 && (
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Matching industries</p>
            )}
            <div className="space-y-2">
              {results.slice(0, 8).map(({ ind, sub, subHits }) => {
                const Icon = ICONS[ind.icon] || Briefcase;
                return (
                  <button key={ind.id + (sub?.slug || "")} data-testid={`industry-result-${ind.slug}`}
                    onClick={() => go(ind, sub)}
                    className="w-full bg-white border border-slate-200 hover:border-emerald-500/50 rounded-xl px-4 py-3.5 flex items-center gap-3 text-left transition-all rise">
                    <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Icon size={18} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-sm text-slate-900">{ind.name}</span>
                      {subHits.length > 0 && <span className="block text-xs text-emerald-700 truncate">{subHits.slice(0, 3).map((s) => s.name).join(" · ")}</span>}
                    </span>
                    <ArrowRight size={16} className="text-slate-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-14">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">
              {helpGroup ? `${helpGroup} — Prompt Categories` : "Browse All Prompt Categories"}
            </h2>
            {helpGroup && (
              <button onClick={() => setHelpGroup("")} data-testid="help-group-clear" className="text-sm font-bold text-emerald-700 hover:text-emerald-800">
                Show all {categories.length} categories
              </button>
            )}
          </div>

          <ShowcaseGallery category="Cinematic & Camera Shots"
            headline="Cinematic shots — made by users with /commands"
            subheadline="Every image below was generated using one of our Cinematic & Camera Shots prompts. Tap to enlarge."
            dataTestid="cinematic-showcase" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="prompt-category-grid">
            {shownCategories.map((c) => {
              const locked = isCategoryLocked(c.name);
              return (
              <div key={c.id} data-testid={`prompt-category-card-${c.slug}`} onClick={() => goCategory(c.name)}
                className={`bg-white border rounded-2xl p-5 flex flex-col cursor-pointer transition-all group ${locked ? "border-slate-200 opacity-70 hover:opacity-100 hover:border-[#D4AF37]/60" : "border-slate-200 hover:border-emerald-500/50 hover:shadow-md"}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-sm">{c.name}</h3>
                  {locked ? (
                    <span data-testid={`cat-locked-badge-${c.slug}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold bg-[#0B0F0D] text-[#D4AF37] rounded-full px-2 py-0.5">
                      <Lock size={9} /> LOCKED
                    </span>
                  ) : (
                    <span className="shrink-0 text-[10px] font-bold bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">{c.count} shortcuts</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed flex-1">{CATEGORY_META[c.name] || "Ready-made AI short-command prompts for this area."}</p>
                <span className={`mt-3 text-sm font-semibold flex items-center gap-1 ${locked ? "text-[#B8962E]" : "text-emerald-700"}`}>
                  {locked ? "Unlock with Pro" : isMember ? "Open in Dictionary" : "Try free for 24 hours"} <ArrowRight size={14} />
                </span>
              </div>
            );})}
          </div>
        </div>

        <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-6">Browse by Industry</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="industry-grid">
          {industries.map((ind) => {
            const Icon = ICONS[ind.icon] || Briefcase;
            return (
              <div key={ind.id} data-testid={`industry-card-${ind.slug}`} onClick={() => go(ind, null)}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col cursor-pointer hover:border-emerald-500/50 hover:shadow-md transition-all group">
                <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors"><Icon size={20} /></span>
                <h3 className="font-display font-bold text-sm mt-3.5">{ind.name}</h3>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed flex-1">{ind.description}</p>
                <p className="text-[11px] text-slate-400 mt-2.5 leading-relaxed">{ind.subcategories.slice(0, 5).map((s) => s.name).join(" • ")}</p>
                <span className="mt-3 text-emerald-700 text-sm font-semibold flex items-center gap-1">View Shortcuts <ArrowRight size={14} /></span>
              </div>
            );
          })}
        </div>
        <p data-testid="ai-disclaimer" className="text-xs text-slate-400 text-center mt-12 max-w-2xl mx-auto">
          AI-generated responses may contain errors or inaccuracies. Always verify important information before relying on it.
        </p>
      </div>
    </div>
  );
}
